/**
 * Gameplay relay message handlers — extracted from MultiplayerManager.
 *
 * Handles: key_state, aim_update, fire_action, di_resolve, burst_resolve,
 * special_trigger, defense_resolve, grapple_action, edgeguard_fire,
 * tank_select, turn_snapshot
 */

import { lookupWeapon } from '../weapons';
import { getAllTankIds, getTankShot, getEffectiveShots, lookupTank } from '../tanks';
import { TUMBLE, BRACE, BURST, DI, LAUNCH_FX, TURN_FLOW, WORLD } from '../constants';
import { fire as fireProjectile } from '../game/aiming';
import { fireRailgunBeam } from '../game/phase-edgeguard';
import { applyEffects, transitionPhase, type Renderers } from '../game/effects';
import { forceGrappleAttach } from '../game/grapple';
import { startBurstWindow } from '../game/burst';
import { startDIWindow, applyDeferredKnockback } from '../game/di';
import { triggerVoidDeathSequence, triggerSkyDeathSequence, triggerSideDeathSequence } from '../game/death';
import { mirrorAirborne, mirrorSettle } from '../physics/transitions';
import { activateNextDefender, unfreezeAllPlayers } from '../game/defense';
import type { GameState } from '../types';
import type { GameEffect } from '../game/effects';
import type { GameClient } from './client';
import type { NetworkInput } from '../input';
import type { NetDebugLog } from './debug-log';
import type { TurnSnapshotPlayer } from './messages';

export interface GameplayHandlerDeps {
    readonly client: GameClient;
    readonly networkInput: NetworkInput;
    readonly recoveryInput: NetworkInput;
    readonly debugLog: NetDebugLog | null;
    getState(): GameState;
    getRenderers(): Renderers | null;
    clearDisconnectGrace(): void;
    setPendingRoomSeed(seed: number): void;
    /** Get current pending checkpoint ID (-1 if not yet at turn boundary). */
    getPendingCheckpointId(): number;
    /** Signal that turn_snapshot has been received. Applies and hashes if checkpoint
     *  already reached, otherwise buffers for application after advanceTurn(). */
    onSnapshotReceived(snapshot: TurnSnapshotPlayer[]): void;
}

/**
 * Apply turn_snapshot player data to game state.
 * Extracted as a helper so it can be called both when the snapshot arrives
 * normally (after advanceTurn) and when a buffered snapshot is applied later.
 *
 * Position/physics: only applied for OPPONENT (corrects turn-owner walking drift).
 * Gameplay fields (damage, momentum): applied for ALL players to sync
 * explosion-result divergence from position drift.
 */
export function applyTurnSnapshotData(
    state: GameState,
    snapshot: TurnSnapshotPlayer[],
    mySlot: number,
    debugLog: NetDebugLog | null,
): void {
    for (let i = 0; i < snapshot.length && i < state.players.length; i++) {
        const p = state.players[i];
        const s = snapshot[i];
        const isMe = (i === mySlot);
        debugLog?.logSnap('turn_snap', state.frameCount, i, p.x, p.y, s.x, s.y);

        // Physics state: full overwrite for opponent, soft position correction for self.
        //
        // Opponent gets a full snap (corrects turn-owner walking drift from key_state latency).
        // Self gets a guarded position snap: only applied when the difference is small (< 20px),
        // which catches post-defense settlement drift (typically 1-5px) but ignores stale
        // pre-defense data that could be 100+ px off in a network race condition.
        // The turn owner only generates the snapshot AFTER receiving defense_resolve (it waits
        // in defenseWaitForAuthority before transitioning to awaiting_next_turn), so the
        // snapshot almost always has correct post-defense data — the threshold is a safety net.
        if (!isMe) {
            p.x = s.x;
            p.y = s.y;
            p.vx = s.vx;
            p.vy = s.vy;
            p.tumble = s.tumble;
            p.settled = s.settled;
            p.liftFuel = s.liftFuel;
            p.liftActive = s.liftActive;
            p.fastFallActive = s.fastFallActive;
            if (s.collapseGrabbed !== undefined) p.collapseGrabbed = s.collapseGrabbed;
            if (s.collapseFrame !== undefined) p.collapseFrame = s.collapseFrame;
            if (s.collapseTargetX !== undefined) p.collapseTargetX = s.collapseTargetX;
            if (s.collapseTargetY !== undefined) p.collapseTargetY = s.collapseTargetY;
            if (s.portalImmunityFrames !== undefined) p.portalImmunityFrames = s.portalImmunityFrames;
            if (s.onWireframe !== undefined) p.onWireframe = s.onWireframe;
            // Stock system fields
            if (s.stocks !== undefined) p.stocks = s.stocks;
            if (s.respawnTimer !== undefined) p.respawnTimer = s.respawnTimer;
            if (s.respawnInvulnFrames !== undefined) p.respawnInvulnFrames = s.respawnInvulnFrames;
        } else {
            // Soft position correction for self — absorbs post-defense settlement drift
            const dx = s.x - p.x;
            const dy = s.y - p.y;
            if (dx * dx + dy * dy < 400) { // < 20px
                p.x = s.x;
                p.y = s.y;
            }
        }

        // Gameplay state: apply for all players
        p.angle = s.angle;
        p.power = s.power;
        p.damagePercent = s.damagePercent;
        p.momentum = s.momentum;
        p.weaponCharge = s.weaponCharge;
        if (s.armedShotTier !== undefined) {
            p.armedShotTier = s.armedShotTier ?? null;
        }
        if (s.cupidMarkTurns !== undefined) p.cupidMarkTurns = s.cupidMarkTurns;
        if (s.cupidMarkedBy !== undefined) p.cupidMarkedBy = s.cupidMarkedBy;

        // Reconstruct actionState from snapshot booleans (opponent only)
        if (!isMe) {
            if (!p.alive) {
                p.actionState = { tag: 'dead' };
            } else if (p.settled) {
                p.actionState = { tag: 'settled', settledX: p.settledX, settledY: p.settledY };
            } else if (p.tumble) {
                p.actionState = p.tumbleRecoveryFrame > 0
                    ? { tag: 'tumble_recovery', recoveryFrame: p.tumbleRecoveryFrame, recoveryMax: p.tumbleRecoveryMax, variant: 'knockback' }
                    : { tag: 'tumble_hitstun', hitstun: p.hitstunFrames, armor: p.launchArmor, variant: 'knockback' };
            } else {
                p.actionState = { tag: 'airborne' };
            }
        }
    }
}

export function registerGameplayHandlers(deps: GameplayHandlerDeps): void {
    const { client } = deps;

    // key_state: primary input relay — opponent's key states each frame
    // NOTE: ACTION.FIRE ('GameFire') is excluded — firing is handled by the explicit
    // fire_action message. Hold-to-charge/release-to-fire: FIRE pulses on trigger release.
    // IMPORTANT: clear() first because captureKeyState() only sends pressed keys (sparse).
    // Without clear(), released keys would persist as phantom presses.
    client.on('key_state', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        deps.clearDisconnectGrace();
        deps.networkInput.clear();
        for (const [key, pressed] of Object.entries(msg.keys)) {
            if (key === 'GameFire') continue; // firing handled by fire_action
            // During edgeguard, also filter GameCharge — edgeguard fire is handled
            // by the explicit edgeguard_fire message, not key_state relay.
            // GameCharge can trigger beam fire in phase-edgeguard.ts; without filtering,
            // both clients fire independently from slightly different positions → desync.
            if (key === 'GameCharge' && state.phase === 'edgeguard') continue;
            // Filter tier-switch keys — these are momentum-gated decisions that can
            // diverge if momentum has drifted between clients. aim_update carries
            // shotTier authoritatively, so the watching client gets the correct tier
            // without re-running the momentum check with potentially different values.
            if (key === 'Digit1' || key === 'Digit2' || key === 'Digit3') continue;
            deps.networkInput.setKey(key, pressed as boolean);
        }
    });

    // recovery_key_state: directional input from non-current tumbling player during awaiting
    // Separate from key_state to avoid clobber (both senders in same frame).
    // Only directional keys are accepted — no fire/charge/special.
    client.on('recovery_key_state', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        deps.clearDisconnectGrace();
        deps.recoveryInput.clear();
        for (const [key, pressed] of Object.entries(msg.keys)) {
            if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'KeyA' || key === 'KeyD') {
                deps.recoveryInput.setKey(key, pressed as boolean);
            }
        }
    });

    // defender_position: authoritative defender position during edgeguard + defensive_movement.
    // Corrections flow FROM defender TO attacker (defender is source of truth).
    // Hard snap — tumble/movement makes visual pops imperceptible.
    client.on('defender_position', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        if (state.phase !== 'edgeguard' && state.phase !== 'defensive_movement') return;
        deps.clearDisconnectGrace();
        const def = state.players[msg.playerIndex];
        if (!def || !def.alive) return;
        deps.debugLog?.logSnap('def_pos', state.frameCount, msg.playerIndex, def.x, def.y, msg.x, msg.y);
        def.x = msg.x;
        def.y = msg.y;
        def.vx = msg.vx;
        def.vy = msg.vy;
    });

    client.on('aim_update', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        // Always use opponent's slot — currentPlayer may have advanced already
        const opponentSlot = 1 - state.multiplayer.mySlot;
        const p = state.players[opponentSlot];
        if (!p) return;

        // Always sync angle and power for aim line preview
        p.angle = msg.angle;
        p.power = msg.power;

        // Adaptive position correction — lerp rate scales with distance.
        // Small diffs (input lag) ignored, medium diffs smooth, large diffs snap.
        // Velocity always hard-snapped — lerping velocity leaves a long tail of
        // physics drift (different vx/vy → different slope costs, different
        // trajectories). Position lerp is visual smoothing; velocity is physics truth.
        const dx = msg.x - p.x;
        const dy = msg.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 150) {
            // Hard snap — something went very wrong
            p.x = msg.x;
            p.y = msg.y;
        } else if (dist > 60) {
            // Moderate correction (grapple drift, etc.)
            p.x += dx * 0.4;
            p.y += dy * 0.4;
        } else if (dist > 30) {
            // Gentle correction — barely noticeable
            p.x += dx * 0.15;
            p.y += dy * 0.15;
        }
        // Always snap velocity — feeds into physics, must be exact
        p.vx = msg.vx;
        p.vy = msg.vy;

        // Safe to sync charging — FIRE pulses on trigger release (hold-to-charge model).
        // Opponent sees the charge bar oscillating in real-time.
        p.charging = msg.charging;

        // Sync shot tier selection — authoritative (Digit1/2/3 filtered from key_state)
        if (msg.shotTier != null) {
          const tier = msg.shotTier!;
          const shots = getEffectiveShots(p.tankId, p.terraformerMode);
          if (tier >= 1 && tier <= shots.length) {
            p.selectedShotTier = tier;
            p.selectedWeapon = getTankShot(p.tankId, tier, p.terraformerMode).weapon;
          }
        }

        // Sync momentum — prevents drift from position lerp causing different
        // slope costs. Hard-snap to authoritative value (momentum is not visual).
        if (msg.momentum != null) {
          p.momentum = msg.momentum;
        }
    });

    client.on('fire_action', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        if (import.meta.env.DEV) console.log('[NET] Opponent fired:', msg);
        const opponentSlot = 1 - state.multiplayer.mySlot;
        const p = state.players[opponentSlot];
        if (!p) return;
        // Snap target positions so hit detection is deterministic —
        // settlement micro-drift can cause grazing shots to hit on one
        // client and miss on the other without this correction.
        if (msg.targets) {
            for (const t of msg.targets) {
                const tp = state.players[t.playerIndex];
                if (tp) {
                    deps.debugLog?.logSnap('fire_tgt', state.frameCount, t.playerIndex, tp.x, tp.y, t.x, t.y);
                    tp.x = t.x;
                    tp.y = t.y;
                }
            }
        }
        // Set position, velocity, and aim to match opponent's fire
        deps.debugLog?.logSnap('fire_src', state.frameCount, opponentSlot, p.x, p.y, msg.x, msg.y);
        p.x = msg.x;
        p.y = msg.y;
        p.vx = msg.vx;
        p.vy = msg.vy;
        p.angle = msg.angle;
        p.power = msg.power;
        p.charging = false;
        // Apply weapon from fire_action message
        if (msg.weaponId && msg.weaponId !== 'default') {
          p.selectedWeapon = lookupWeapon(msg.weaponId);
        }
        // Call fire() directly to create the projectile
        fireProjectile(state);
        // Transition to firing phase
        state.phase = 'firing';
        deps.debugLog?.addEvent('fire_action', state.frameCount, `P${opponentSlot} a=${msg.angle.toFixed(1)} p=${msg.power.toFixed(0)}`, 'info');
    });

    // Firing resolve: authoritative post-firing knockback state from opponent.
    // Snaps pending knockback for all knocked players to match authority,
    // eliminating multi-hit accumulation divergence (scatter, etc.).
    // Also carries authoritative DI decision to prevent DI window desync.
    client.on('firing_resolve', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        for (const snap of msg.players) {
            const p = state.players[snap.playerIndex];
            if (!p) continue;
            deps.debugLog?.logSnap('firing_res', state.frameCount, snap.playerIndex, p.x, p.y, snap.x, snap.y);
            p.x = snap.x;
            p.y = snap.y;
            // Fix 2: Guard diPendingVx/Vy against re-setting cleared values.
            // If local already applied knockback (applyDeferredKnockback cleared them),
            // don't restore them — that would re-activate the DI freeze gate in physics/tanks.ts.
            // Exception: authority says DI should open, meaning we need to re-enter DI.
            const localAlreadyApplied = (p.diPendingVx === 0 && p.diPendingVy === 0);
            if (!localAlreadyApplied || msg.diOpened === true) {
                p.diPendingVx = snap.diPendingVx;
                p.diPendingVy = snap.diPendingVy;
            }
            p.kbStackHitCount = snap.kbStackHitCount;
            p.damagePercent = snap.damagePercent;
            p.weaponCharge = snap.weaponCharge;
        }

        // Fix 1d: Reconcile authoritative DI decision with local state.
        // If still in firing phase, local hasn't decided yet — no reconciliation needed
        // (Fix 3 rounding ensures local decision will match).
        // Only reconcile when there's an actual mismatch.
        if (msg.diOpened === true && state.phase === 'awaiting_next_turn') {
            // Authority opened DI but local skipped it — re-enter DI window
            const knockedIndices = msg.players.map(s => s.playerIndex);
            const diOpened = startDIWindow(state, knockedIndices);
            if (diOpened) {
                const fx: GameEffect[] = [];
                transitionPhase(state, 'di_window', fx);
                fx.push({ type: 'di_show' });
                fx.push({ type: 'screen_flash', color: DI.ENTRY_FLASH_COLOR, alpha: DI.ENTRY_FLASH_ALPHA });
                const diPlayer = state.players[state.diActivePlayer];
                if (diPlayer) {
                    fx.push({ type: 'particles', method: 'spawnDIEntry', args: [diPlayer.x, diPlayer.y, diPlayer.color] });
                    fx.push({ type: 'camera_follow', x: diPlayer.x, y: diPlayer.y });
                }
                state.diWaitForAuthority = true;
                const renderers = deps.getRenderers();
                if (renderers) applyEffects(fx, renderers);
                if (import.meta.env.DEV) console.log(`[NET] DI reconciliation: authority opened DI, local was awaiting — entering DI window`);
            }
        } else if (msg.diOpened === false && state.phase === 'di_window') {
            // Authority skipped DI but local opened it — cancel DI, apply knockback directly
            for (const snap of msg.players) {
                const p = state.players[snap.playerIndex];
                if (!p || !p.alive) continue;
                applyDeferredKnockback(p);
            }
            state.diActivePlayer = -1;
            state.diHitQueue = [];
            state.diWaitForAuthority = false;
            const fx: GameEffect[] = [];
            fx.push({ type: 'di_hide' });
            transitionPhase(state, 'awaiting_next_turn', fx);
            state.turnTimer = TURN_FLOW.TURN_DELAY;
            const renderers = deps.getRenderers();
            if (renderers) applyEffects(fx, renderers);
            if (import.meta.env.DEV) console.log(`[NET] DI reconciliation: authority skipped DI, local had opened — cancelling DI window`);
        }

        if (import.meta.env.DEV) console.log(`[NET] Firing resolve received: ${msg.players.length} knocked players, diOpened=${msg.diOpened}`);
        deps.debugLog?.addEvent('firing_resolve', state.frameCount, `${msg.players.length} knocked di=${msg.diOpened}`, 'info');
    });

    // DI resolve: authoritative post-DI velocity + position from opponent.
    // On the non-DI client, this is the ONLY source of truth — local resolve was skipped.
    client.on('di_resolve', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const p = state.players[msg.playerIndex];
        if (p) {
            // Apply authoritative post-DI state
            deps.debugLog?.logSnap('di_res', state.frameCount, msg.playerIndex, p.x, p.y, msg.x, msg.y);
            p.vx = msg.vx;
            p.vy = msg.vy;
            p.x = msg.x;
            p.y = msg.y;
            p.hitstunFrames = msg.hitstunFrames;
            p.tumble = msg.hitstunFrames > 0;
            p.launchArmor = msg.hitstunFrames > 0 ? TUMBLE.LAUNCH_ARMOR_FRAMES : 0;
            // Recompute recovery from hitstun (total = hitstun / HITSTUN_FRACTION)
            if (msg.hitstunFrames > 0) {
                const total = Math.round(msg.hitstunFrames / TUMBLE.HITSTUN_FRACTION);
                p.tumbleRecoveryMax = total - msg.hitstunFrames;
                p.tumbleRecoveryFrame = 0;
            } else {
                p.tumbleRecoveryMax = 0;
                p.tumbleRecoveryFrame = 0;
            }
            // Clear deferred knockback (was skipped on non-DI client)
            p.diPendingVx = 0;
            p.diPendingVy = 0;
            p.diAngleShift = 0;
            p.kbStackHitCount = 0;
            p.kbStackFirstFrame = 0;
            p.actionState = msg.hitstunFrames > 0
                ? { tag: 'tumble_hitstun', hitstun: msg.hitstunFrames, armor: p.launchArmor, variant: 'knockback' }
                : { tag: 'airborne' };

            // Spawn DI resolve visual effects on opponent's screen
            const renderers = deps.getRenderers();
            if (renderers) {
                const fx: GameEffect[] = [
                    { type: 'screen_flash', color: 0xffffff, alpha: DI.EXIT_FLASH_ALPHA },
                    { type: 'particles', method: 'spawnDIResolve', args: [p.x, p.y, p.vx, p.vy, p.color] },
                    { type: 'tank_flash', playerIndex: msg.playerIndex,
                      bright: LAUNCH_FX.FLASH_BRIGHT, dark: LAUNCH_FX.FLASH_DARK,
                      duration: LAUNCH_FX.FLASH_DURATION },
                ];
                applyEffects(fx, renderers);
            }

            // Unblock the non-DI client's di_window phase
            const authLabel = state.diWaitForAuthority ? 'auth' : 'corr';
            if (state.diWaitForAuthority) {
                state.diWaitForAuthority = false;
                state.diActivePlayer = -1;
                if (import.meta.env.DEV) console.log(`[NET] DI resolve received (authoritative): P${msg.playerIndex} pos=(${msg.x.toFixed(1)}, ${msg.y.toFixed(1)}) vel=(${msg.vx.toFixed(2)}, ${msg.vy.toFixed(2)}) ragdoll=${msg.hitstunFrames}`);
            } else {
                if (import.meta.env.DEV) console.log(`[NET] DI resolve received (correction): P${msg.playerIndex} pos=(${msg.x.toFixed(1)}, ${msg.y.toFixed(1)}) vel=(${msg.vx.toFixed(2)}, ${msg.vy.toFixed(2)}) ragdoll=${msg.hitstunFrames}`);
            }
            deps.debugLog?.addEvent('di_resolve', state.frameCount, `P${msg.playerIndex} ${authLabel} h=${msg.hitstunFrames}`, 'info');
        }
    });

    // Burst resolve: authoritative post-burst velocity + position from opponent.
    client.on('burst_resolve', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const p = state.players[msg.playerIndex];
        if (p) {
            deps.debugLog?.logSnap('burst_res', state.frameCount, msg.playerIndex, p.x, p.y, msg.x, msg.y);
            p.vx = msg.vx;
            p.vy = msg.vy;
            p.x = msg.x;
            p.y = msg.y;
            p.momentum = msg.momentum;
            p.weaponCharge = msg.weaponCharge;
            p.settled = false;
            p.burstInputX = 0;
            p.burstInputY = 0;

            if (msg.braced) {
                // Brace — apply authoritative damagePercent, no ragdoll/burstFlying
                p.damagePercent = msg.damagePercent;
                if (p.settled) mirrorSettle(p); else mirrorAirborne(p);
                // Spawn brace visual effects on opponent's screen
                const renderers = deps.getRenderers();
                if (renderers) {
                    applyEffects([
                        { type: 'screen_flash', color: BRACE.FLASH_COLOR, alpha: BRACE.FLASH_ALPHA },
                        { type: 'screen_shake', intensity: BRACE.SHAKE, decay: BRACE.SHAKE_DECAY },
                        { type: 'particles', method: 'spawnBraceHeal', args: [p.x, p.y, p.color] },
                        { type: 'tank_flash', playerIndex: msg.playerIndex, bright: BRACE.GREEN_BRIGHT, dark: BRACE.GREEN_DARK, duration: 0.25 },
                        { type: 'hud_heal_flash', playerIndex: msg.playerIndex },
                    ], renderers);
                }
            } else {
                // Normal burst — apply ragdoll + flight trail (no recovery — control snap)
                if (msg.hitstunFrames > 0) {
                    p.tumble = true;
                    p.hitstunFrames = msg.hitstunFrames;
                    p.launchArmor = TUMBLE.LAUNCH_ARMOR_FRAMES;
                    p.tumbleRecoveryMax = 0;
                    p.tumbleRecoveryFrame = 0;
                    p.actionState = { tag: 'tumble_hitstun', hitstun: msg.hitstunFrames, armor: p.launchArmor, variant: 'burst' };
                } else {
                    mirrorAirborne(p);
                }
                p.burstFlying = true;
                p.burstTrailCooldown = 0;

                // Spawn burst resolve visual effects on opponent's screen
                const renderers = deps.getRenderers();
                if (renderers) {
                    const hasDirection = Math.abs(p.vx) > 0.5 || Math.abs(p.vy) > 0.5;
                    const shakeIntensity = hasDirection ? BURST.RESOLVE_SHAKE_FULL : BURST.RESOLVE_SHAKE_NONE;
                    const fx: GameEffect[] = [
                        { type: 'screen_flash', color: BURST.GOLD, alpha: BURST.EXIT_FLASH_ALPHA },
                        { type: 'screen_shake', intensity: shakeIntensity, decay: BURST.RESOLVE_SHAKE_DECAY },
                        { type: 'particles', method: 'spawnBurstResolve', args: [p.x, p.y, p.vx, p.vy, p.color] },
                    ];
                    if (hasDirection) {
                        fx.push({
                            type: 'tank_flash', playerIndex: msg.playerIndex,
                            bright: BURST.GOLD_BRIGHT, dark: BURST.GOLD_DARK,
                            duration: 0.25,
                        });
                    }
                    applyEffects(fx, renderers);
                }
            }

            const burstAuthLabel = state.burstWaitForAuthority ? 'auth' : 'corr';
            if (state.burstWaitForAuthority) {
                state.burstWaitForAuthority = false;
                state.burstActivePlayer = -1;
                if (import.meta.env.DEV) console.log(`[NET] Burst resolve received (authoritative): P${msg.playerIndex} pos=(${msg.x.toFixed(1)}, ${msg.y.toFixed(1)}) vel=(${msg.vx.toFixed(2)}, ${msg.vy.toFixed(2)}) momentum=${msg.momentum} ragdoll=${msg.hitstunFrames} braced=${msg.braced}`);
            } else {
                if (import.meta.env.DEV) console.log(`[NET] Burst resolve received (correction): P${msg.playerIndex}`);
            }
            deps.debugLog?.addEvent('burst_resolve', state.frameCount, `P${msg.playerIndex} ${burstAuthLabel} ${msg.braced ? 'BRACE' : 'burst'} m=${msg.momentum.toFixed(0)}`, 'info');
        }
    });

    // Special trigger: opponent activated voluntary burst during aiming or defensive movement
    client.on('special_trigger', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;

        // Accept during aiming (offensive special) OR defensive_movement (defense burst)
        if (state.phase !== 'aiming' && state.phase !== 'defensive_movement') return;

        const p = state.players[msg.playerIndex];

        if (state.phase === 'aiming') {
            // Offensive special — existing logic
            p.specialUsed = true;
            p.charging = false;
            p.power = 0;
            p.chargeGraceTimer = 0;

            state.burstFromSpecial = true;
            startBurstWindow(state, msg.playerIndex);
        } else {
            // Defense burst from opponent
            p.specialUsed = true;
            state.burstFromDefense = true;
            state.burstFromSpecial = false;
            startBurstWindow(state, msg.playerIndex);
        }

        // Phase transition + visual effects
        const fx: GameEffect[] = [];
        transitionPhase(state, 'burst_window', fx);
        fx.push({ type: 'burst_show' });
        fx.push({ type: 'screen_flash', color: BURST.ENTRY_FLASH_COLOR, alpha: BURST.ENTRY_FLASH_ALPHA });
        fx.push({ type: 'screen_shake', intensity: BURST.ENTRY_SHAKE, decay: BURST.ENTRY_SHAKE_DECAY });
        fx.push({ type: 'particles', method: 'spawnBurstEntry', args: [p.x, p.y, p.color] });
        const renderers = deps.getRenderers();
        if (renderers) {
            applyEffects(fx, renderers);
        }
        deps.debugLog?.addEvent('special_trigger', state.frameCount, `P${msg.playerIndex} ${state.phase === 'aiming' ? 'offensive' : 'defense'}`, 'info');
    });

    // Defense resolve: authoritative post-defense position from opponent.
    // On non-authority, advances the defense queue (next defender or transition out).
    client.on('defense_resolve', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const p = state.players[msg.playerIndex];
        if (p) {
            deps.debugLog?.logSnap('def_res', state.frameCount, msg.playerIndex, p.x, p.y, msg.x, msg.y);
            p.vx = msg.vx;
            p.vy = msg.vy;
            p.x = msg.x;
            p.y = msg.y;
            p.tumble = msg.tumble;
            p.hitstunFrames = msg.hitstunFrames;
            p.tumbleRecoveryFrame = msg.tumbleRecoveryFrame;
            p.tumbleRecoveryMax = msg.tumbleRecoveryMax;
            p.momentum = msg.momentum;
            p.weaponCharge = msg.weaponCharge;
            p.settled = msg.settled;
            p.grounded = msg.grounded;
            p.liftFuel = msg.liftFuel;
            p.liftActive = msg.liftActive;
            p.fastFallActive = msg.fastFallActive;
            p.landingTimer = msg.landingTimer;
            p.landingTimerMax = msg.landingTimerMax;
            p.defenseSubPhase = msg.defenseSubPhase;
            if (msg.onWireframe !== undefined) p.onWireframe = msg.onWireframe;

            // Reconstruct actionState from authoritative fields
            if (p.settled) {
                p.actionState = { tag: 'settled', settledX: p.x, settledY: p.y };
                p.settledX = p.x;
                p.settledY = p.y;
            } else if (p.tumble) {
                p.actionState = p.tumbleRecoveryFrame > 0
                    ? { tag: 'tumble_recovery', recoveryFrame: p.tumbleRecoveryFrame, recoveryMax: p.tumbleRecoveryMax, variant: 'knockback' }
                    : { tag: 'tumble_hitstun', hitstun: p.hitstunFrames, armor: p.launchArmor, variant: 'knockback' };
            } else if (!p.grounded) {
                p.actionState = { tag: 'airborne' };
            } else {
                p.actionState = { tag: 'walking', catchupTimer: 0 };
            }

            if (import.meta.env.DEV) console.log(`[NET] Defense resolve received: P${msg.playerIndex} pos=(${msg.x.toFixed(1)}, ${msg.y.toFixed(1)}) vel=(${msg.vx.toFixed(2)}, ${msg.vy.toFixed(2)}) tumble=${msg.tumble} momentum=${msg.momentum.toFixed(1)}`);
            deps.debugLog?.addEvent('defense_resolve', state.frameCount, `P${msg.playerIndex} m=${msg.momentum.toFixed(0)} ${msg.tumble ? 'tumble' : 'ok'}`, 'info');
        }

        // Advance the defense queue on non-authority
        if (state.defenseWaitForAuthority) {
            state.defenseWaitForAuthority = false;

            if (state.defenseQueue.length > 0) {
                // More defenders — activate next
                const fx: GameEffect[] = [];
                activateNextDefender(state, fx);
                const renderers = deps.getRenderers();
                if (renderers) applyEffects(fx, renderers);

                // If next defender is also not me, keep waiting
                if (state.defenseActivePlayer >= 0 &&
                    state.defenseActivePlayer !== state.multiplayer!.mySlot) {
                    state.defenseWaitForAuthority = true;
                }
            } else {
                // All defenders done — unfreeze and transition
                unfreezeAllPlayers(state);
                state.defenseActivePlayer = -1;
                const fx: GameEffect[] = [];
                transitionPhase(state, 'awaiting_next_turn', fx);
                state.turnTimer = TURN_FLOW.TURN_DELAY;
                const renderers = deps.getRenderers();
                if (renderers) applyEffects(fx, renderers);
            }
        }
    });

    client.on('grapple_action', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        if (import.meta.env.DEV) console.log('[NET] Opponent grapple:', msg);
        deps.debugLog?.addEvent('grapple_action', state.frameCount, msg.action, 'info');

        // Grapple cancel — opponent's grapple ended, clear local state so rope fades
        if (msg.action === 'cancel') {
            state.grapple.active = null;
            return;
        }

        const active = state.grapple.active;
        if (!active) return; // no grapple to sync — G key relay will create it
        if (msg.anchorX === undefined || msg.anchorY === undefined) return;

        const anchorX = msg.anchorX;
        const anchorY = msg.anchorY;

        // Snap player position to match authority
        if (msg.playerX !== undefined && msg.playerY !== undefined) {
            const opponentSlot = 1 - state.multiplayer!.mySlot;
            const p = state.players[opponentSlot];
            if (p) {
                p.x = msg.playerX;
                p.y = msg.playerY;
            }
        }

        if (active.phase === 'traveling') {
            // Case A: hook still traveling on remote — force-transition to retracting
            // with authoritative anchor. Consume ammo (traveling hasn't consumed it yet).
            state.grapple.ammo[active.playerIndex]--;
            forceGrappleAttach(state, anchorX, anchorY);
        } else if (active.phase === 'retracting') {
            // Case B: already retracting — correct anchor position
            active.anchorX = anchorX;
            active.anchorY = anchorY;
            // Snap pinned rope particle to corrected anchor
            if (active.rope.length > 0 && active.rope[0].pinned) {
                active.rope[0].x = anchorX;
                active.rope[0].y = anchorY;
                active.rope[0].prevX = anchorX;
                active.rope[0].prevY = anchorY;
            }
        }
    });

    // Edgeguard fire: authoritative beam fire from opponent.
    // GameCharge is filtered from key_state during edgeguard to prevent
    // the non-authority from firing independently. This handler is the
    // sole source of beam fire on the non-attacker's client.
    client.on('edgeguard_fire', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        if (state.phase !== 'edgeguard') return;

        const opponentSlot = 1 - state.multiplayer.mySlot;
        const attacker = state.players[opponentSlot];
        if (!attacker) return;

        // Snap attacker to authoritative fire position/angle
        attacker.x = msg.x;
        attacker.y = msg.y;
        attacker.angle = msg.angle;

        // Snap target positions/state so hit detection matches the attacker's client.
        // During tumble, targets drift a few pixels between clients — without this,
        // the beam hits on one client and misses on the other.
        if (msg.targets) {
            for (const snap of msg.targets) {
                const p = state.players[snap.playerIndex];
                if (!p) continue;
                deps.debugLog?.logSnap('eg_tgt', state.frameCount, snap.playerIndex, p.x, p.y, snap.x, snap.y);
                p.x = snap.x;
                p.y = snap.y;
                p.momentum = snap.momentum;
                p.damagePercent = snap.damagePercent;
            }
        }

        // Fire beam with authoritative data — modifies state and pushes effects
        const beamFx: GameEffect[] = [];
        fireRailgunBeam(state, attacker, beamFx);
        const renderers = deps.getRenderers();
        if (renderers) {
            applyEffects(beamFx, renderers);
        }

        if (import.meta.env.DEV) console.log(`[NET] Edgeguard fire received: angle=${msg.angle.toFixed(2)} pos=(${msg.x.toFixed(1)}, ${msg.y.toFixed(1)})`);
        deps.debugLog?.addEvent('edgeguard_fire', state.frameCount, `a=${msg.angle.toFixed(1)}`, 'info');
    });

    // Death trigger: redundant safety net from opponent's client.
    // If local client already detected the death (pendingDeath), this is a no-op.
    // If local client missed it due to position drift, this catches the miss.
    client.on('death_trigger', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const p = state.players[msg.playerIndex];
        if (!p || !p.alive || p.pendingDeath) return;

        // Snap to authoritative position
        deps.debugLog?.logSnap('death', state.frameCount, msg.playerIndex, p.x, p.y, msg.x, msg.y);
        p.x = msg.x;
        p.y = msg.y;

        const renderers = deps.getRenderers();

        if (msg.deathType === 'void') {
            const deathFx = triggerVoidDeathSequence(state, msg.playerIndex);
            if (renderers) applyEffects(deathFx, renderers);
        } else if (msg.deathType === 'sky') {
            const deathFx = triggerSkyDeathSequence(state, msg.playerIndex);
            if (renderers) applyEffects(deathFx, renderers);
        } else if (msg.deathType === 'side') {
            const side = msg.x < (WORLD.WORLD_LEFT + WORLD.WORLD_RIGHT) / 2 ? 'left' as const : 'right' as const;
            const deathFx = triggerSideDeathSequence(state, msg.playerIndex, side);
            if (renderers) applyEffects(deathFx, renderers);
        } else if (msg.deathType === 'hp') {
            p.pendingDeath = true;
        }

        if (import.meta.env.DEV) console.log(`[NET] Death trigger received: P${msg.playerIndex} ${msg.deathType} at (${msg.x.toFixed(1)}, ${msg.y.toFixed(1)})`);
        deps.debugLog?.addEvent('snap_correction', state.frameCount, `death P${msg.playerIndex} ${msg.deathType}`, 'warn');
    });

    // Tank select: opponent confirmed their tank
    client.on('tank_select', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const opponentSlot = 1 - state.multiplayer.mySlot;
        const p = state.players[opponentSlot];
        if (p && msg.tankId) {
            p.tankId = msg.tankId as string;
            p.color = lookupTank(p.tankId).visual.bodyColor;
            if (msg.terraformerMode) {
                p.terraformerMode = msg.terraformerMode as 'sapper' | 'mason';
            }
            // Update cursor to match opponent's selected tank in the grid
            const tankIds = getAllTankIds();
            const idx = tankIds.indexOf(p.tankId);
            if (idx !== -1) state.tankSelectCursor[opponentSlot] = idx;
            state.tankSelectReady[opponentSlot] = true;
            if (import.meta.env.DEV) console.log(`[NET] Opponent selected tank: ${msg.tankId} (slot=${opponentSlot}, ready=${JSON.stringify(state.tankSelectReady)})`);
        }
    });

    // Stage select: opponent (chooser) confirmed a stage
    client.on('stage_select', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        state.stageId = msg.stageId;
        // For random stages, override pendingRoomSeed with the chooser's preview seed
        if (msg.seed != null) {
            deps.setPendingRoomSeed(msg.seed);
        }
        // Apply theme change visually
        if (msg.stageId) {
            const renderers = deps.getRenderers();
            if (renderers) {
                applyEffects([{ type: 'stage_theme_change', stageId: msg.stageId }], renderers);
            }
        } else if (msg.themeId) {
            const renderers = deps.getRenderers();
            if (renderers) {
                applyEffects([{ type: 'stage_theme_change', stageId: msg.themeId }], renderers);
            }
        }
        state.stageSelectReady = true;
        if (import.meta.env.DEV) console.log(`[NET] Opponent selected stage: ${msg.stageId || 'random'} (seed: ${msg.seed}, theme: ${msg.themeId})`);
    });

    // Turn boundary snapshot: authoritative position sync at turn end.
    //
    // RACE CONDITION GUARD: The turn_snapshot can arrive BEFORE the non-turn-owner's
    // advanceTurn() runs (turn owner settles faster after defense). If we apply the
    // snapshot immediately, advanceTurn() double-applies carry-over to momentum/weaponCharge
    // (snapshot carries post-carry-over values, then advanceTurn applies carry-over again).
    // If we hash immediately, the hash is computed pre-advanceTurn and mismatches.
    //
    // Fix: if the non-turn-owner hasn't reached its turn_boundary_checkpoint yet
    // (pendingCheckpointId < 0), buffer the snapshot. It will be applied after
    // advanceTurn() when the checkpoint fires.
    client.on('turn_snapshot', (msg) => {
        const state = deps.getState();
        if (!state.multiplayer?.enabled) return;
        const snapshot = msg.players;
        if (!snapshot) return;

        deps.debugLog?.addEvent('turn_snapshot', state.frameCount, `${snapshot.length} players`, 'info');

        // Delegate to multiplayer manager — handles both normal and buffered paths
        deps.onSnapshotReceived(snapshot);
    });
}
